import React, { useEffect, useState } from "react";
import SimpleTable from "views/Components/SimpleTable";
import ActionDialog from "views/Components/ActionDialog";
import CredentialsModal from "./CredentialsModal";

import DeleteIcon from "@mui/icons-material/DeleteOutline";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import FileCopyOutlinedIcon from "@mui/icons-material/FileCopyOutlined";

import { IconButton, Button, Tooltip } from "@mui/material";

import { copyTextToClipboard } from "utils";
import AlertMessage, { useAlertMessage } from "views/Components/AlertMessage";

import { PublicApiAPI } from "lib/api/PublicApiAPI";
import { PublicApi } from "lib/models/PublicApi";
import moment from "moment-timezone";

const publicApi = new PublicApiAPI();

const PublicAPITable = (props) => {
  const [isGeneratedCredsDialogOpen, setIsGeneratedCredsDialogOpen] =
    useState(false);
  const [isRotateConfirmOpen, setIsRotateConfirmOpen] = useState(false);
  const [isRotatedSecretDialogOpen, setIsRotatedSecretDialogOpen] =
    useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [alertMessageProps, displayMessage] = useAlertMessage();
  const [apiCredentials, setApiCredentials] = useState([]);
  const [isGeneratingCredentials, setIsGeneratingCredentials] = useState(false);
  const [isDeletingCredentials, setIsDeletingCredentials] = useState(false);
  const [isRotatingSecret, setIsRotatingSecret] = useState(false);

  const fetchApiCredentials = async () => {
    await publicApi
      .getApiCredentials(props.orgId)
      .then((creds) => {
        if (creds) {
          setApiCredentials(creds.data.credentials);
        }
      })
      .catch((error) => {
        displayMessage({
          severity: "error",
          message: `${error}`, // TODO: Figure out if we should use the actual error or a generic message
          noAutoHide: true,
        });
      });
  };

  useEffect(() => {
    fetchApiCredentials();
  }, []);

  const columns = [
    {
      title: "Client ID",
      field: "clientId",
      renderValue: (column, model) => renderClientId(model, column),
    },
    {
      title: "Client ID Generated",
      field: "created",
      renderValue: (column, model) => renderClientIdGenerated(column, model),
    },
    {
      title: "Client Secret",
      field: "clientSecret",
      renderValue: () => renderClientSecret(),
      tooltip:
        "After a Client Secret is rotated, all authorized apps will need to be updated with the new Client Secret.",
    },
    {
      title: "Client Secret Generated",
      field: "modified",
      renderValue: (column, model) =>
        renderClientSecretGenerated(column, model),
    },
  ];

  const toggleGeneratedCredsDialog = () => {
    setIsGeneratedCredsDialogOpen(!isGeneratedCredsDialogOpen);
  };

  const handleSecretRotation = async () => {
    setIsRotatingSecret(true);

    await publicApi
      .rotateApiSecret(props.orgId)
      .then((creds) => {
        if (creds) {
          setApiCredentials(creds.data.credentials);
          setIsRotateConfirmOpen(false);
          setIsRotatedSecretDialogOpen(true);
          setIsRotatingSecret(false);
        }
      })
      .catch((error) => {
        displayMessage({
          severity: "error",
          message: `${error}`, // TODO: Figure out if we should use the actual error or a generic message
          noAutoHide: true,
        });
        setIsRotatingSecret(false);
      });
  };

  const toggleRotatedSecretDialog = () => {
    setIsRotatedSecretDialogOpen(!isRotatedSecretDialogOpen);
  };

  const renderClientId = (model, column) => {
    const value = model[column.field];

    return (
      <div>
        {value}
        <Tooltip title={"Copy Client Id"}>
          <IconButton
            sx={{ marginLeft: "10px" }}
            onClick={() => handleCopyCredentials(value, false)}
            size="small"
          >
            <FileCopyOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </div>
    );
  };

  const renderClientSecret = () => {
    // Always show the made up value as a placeholder for security reasons even tho the customer only sees dots not the actual value.
    const renderValue =
      "111111111111111111111_444444-222222-33333333333333333333333333";
    return (
      <div style={{ display: "flex", alignItems: "center" }}>
        <p style={{ fontFamily: "text-security-disc", marginBottom: 0 }}>
          {renderValue}
        </p>
        <Button
          sx={{ marginLeft: "20px", backgroundColor: "#B60000" }}
          onClick={() => setIsRotateConfirmOpen(true)}
          variant={"contained"}
          datacy={"rotateApiCredentialsButton"}
        >
          Rotate
        </Button>
      </div>
    );
  };

  const renderClientIdGenerated = (column, model) => {
    return moment
      .utc(model[column.field])
      .tz(moment.tz.guess(true))
      .format("lll z");
  };

  const renderClientSecretGenerated = (column, model) => {
    // If the secret has been rotated, then use the modified date
    if (model[column.field]) {
      return moment
        .utc(model[column.field])
        .tz(moment.tz.guess(true))
        .format("lll z");
      // Otherwise use the created date
    } else {
      return moment
        .utc(model["created"])
        .tz(moment.tz.guess(true))
        .format("lll z");
    }
  };

  const handleCopyCredentials = (value, isSecret) => {
    const isSecretCopiedValue = isSecret ? "Client Secret" : "Client ID";

    copyTextToClipboard(value);
    displayMessage({
      severity: "success",
      message: `${isSecretCopiedValue} copied.`,
      noAutoHide: false,
    });
  };

  const handleCredentialDeletion = () => {
    const apiCredentialsModel = new PublicApi();
    const body = {
      id: props.orgId,
    };

    setIsDeletingCredentials(true);
    apiCredentialsModel.set(body);

    apiCredentialsModel
      .delete()
      .then(() => {
        // Refetch the credentials in order to refresh what is displayed in the table without completely refreshing the page
        fetchApiCredentials();
        setIsDeleteConfirmOpen(false);

        // Make sure modal closes before resetting the button state
        // This will make sure the modal doesn't flash on screen
        setTimeout(() => {
          setIsDeletingCredentials(false);
        }, 1000);
      })
      .catch((error) => {
        displayMessage({
          severity: "error",
          message: `${error}`, // TODO: Figure out if we should use the actual error or a generic message
          noAutoHide: true,
        });
        setIsDeletingCredentials(false);
      });
  };

  const isGenerateButtonDisabled = () => {
    if (isGeneratingCredentials) {
      return true;
    } else if (apiCredentials.length > 0) {
      return true;
    } else {
      return false;
    }
  };

  const handleGenerateApiCredentials = () => {
    const apiCredentialsModel = new PublicApi();
    const body = {
      id: props.orgId,
    };

    setIsGeneratingCredentials(true);
    apiCredentialsModel.set(body);

    apiCredentialsModel
      .create()
      .then((creds) => {
        setApiCredentials(creds.credentials);
        toggleGeneratedCredsDialog();
        setIsGeneratingCredentials(false);
      })
      .catch((error) => {
        displayMessage({
          severity: "error",
          message: `${error}`, // TODO: Figure out if we should use the actual error or a generic message
          noAutoHide: true,
        });
        setIsGeneratingCredentials(false);
      });
  };

  return (
    <>
      <AlertMessage {...alertMessageProps} />
      <SimpleTable
        isNorthStar
        data={apiCredentials}
        columns={columns}
        emptyText={<span>No Client ID</span>}
        title={"Blumira API Credentials"}
        description={
          <p>
            {props.description}{" "}
            <a
              href={props.guideUrl}
              target={"_blank"}
              rel={"noopener noreferrer"}
            >
              Blumira Setup Guide <OpenInNewIcon />
            </a>
          </p>
        }
        // isFetching={!ready}
        toolbarActions={[
          {
            icon: AddCircleIcon,
            tooltip: isGeneratingCredentials
              ? "Generating API Credentials..."
              : "Generate API Credentials",
            onClick: (event) => {
              handleGenerateApiCredentials();
            },
            disabled: isGenerateButtonDisabled(),
            dataCy: "generateApiCredentialsButton",
          },
        ]}
        actions={[
          {
            icon: DeleteIcon,
            tooltip: "Delete API Credentials",
            onClick: (event, model) => {
              setIsDeleteConfirmOpen(true);
            },
            datacy: "deleteApiCredentialsButton",
          },
        ]}
        isTableSearchable={false}
      />

      <CredentialsModal
        openModal={isGeneratedCredsDialogOpen}
        toggleModal={toggleGeneratedCredsDialog}
        title={"Generated API Credentials"}
        description={"You've generated a new Client ID and Client Secret."}
        alert={
          <p>
            Please copy both immediately.{" "}
            <strong>
              You will not be able to see the Client Secret again once you close
              this window.
            </strong>
          </p>
        }
        showClientId={true}
        copyCredentials={handleCopyCredentials}
        credentials={apiCredentials}
        datacy={"generatedApiCredentialsModal"}
      />

      <CredentialsModal
        openModal={isRotatedSecretDialogOpen}
        toggleModal={toggleRotatedSecretDialog}
        title={"Rotated Client Secret"}
        description={
          "You've generated a new Client Secret. After a Client Secret is rotated, all authorized apps will need to be updated with the new Client Secret."
        }
        alert={
          <p>
            Please copy the Client Secret immediately.{" "}
            <strong>
              You will not be able to see it again once you close this window.
            </strong>
          </p>
        }
        showClientId={false}
        copyCredentials={handleCopyCredentials}
        credentials={apiCredentials}
        datacy={"rotatedApiSecretModal"}
      />

      <ActionDialog
        open={isRotateConfirmOpen}
        title={"Client Secret Rotation"}
        description={
          <div>
            <p>
              Please confirm that you would like to rotate the Client Secret.{" "}
              <strong>
                After a Client Secret is rotated, all authorized apps will need
                to be updated with the new client secret.
              </strong>{" "}
              If you click “Yes” you will be given a new Client Secret on the
              next screen.
            </p>
            <p>Would you like to proceed?</p>
          </div>
        }
        actions={[
          {
            title: "Cancel",
            action: () => setIsRotateConfirmOpen(false),
          },
          {
            title: isRotatingSecret ? "Rotating..." : "Yes",
            action: handleSecretRotation,
            variant: "contained",
            disabled: isRotatingSecret,
            datacy: "confirmRotateApiSecretButton",
          },
        ]}
        datacy={"rotateApiSecretConfirmModal"}
      />

      <ActionDialog
        open={isDeleteConfirmOpen}
        title={"Delete API Credentials"}
        description={
          <div>
            <p>
              All apps using these credentials will stop working upon deletion.
            </p>
          </div>
        }
        actions={[
          {
            title: "Cancel",
            action: () => setIsDeleteConfirmOpen(false),
          },
          {
            title: isDeletingCredentials ? "Deleting..." : "Delete",
            action: handleCredentialDeletion,
            variant: "contained",
            backgroundColor: isDeletingCredentials ? "primary" : "#B60000",
            disabled: isDeletingCredentials,
            datacy: "deleteApiCredentialsConfirmButton",
          },
        ]}
        datacy={"deleteApiCredentialsConfirmModal"}
      />
    </>
  );
};

export default PublicAPITable;
